import React, { useCallback, useEffect, useState } from "react";
import {
  SwarovskiOptikSegmentShort,
  getIsoCodeWithIntFallback,
  type ImageWithFocalPointType,
  type VideoType,
} from "@swo/shared";
import { ReactTFC } from "../../utils/fp/missing-ts-parts";
import { storefrontClient } from "../../domain/storefront";
import { NewsletterPopupSignUp } from "./newsletter-popup-sign-up";
import { NewsletterPopupThankYou } from "./newsletter-popup-thank-you";
import { SwarovskiOptikLocaleFrontend, type NewsletterTranslation, type PageNewsletterData } from "@swo/storefront";
import { Translated, t2 } from "../../domain/translations/translations";
import { SwarovskiModalWithVisualWrapper } from "../../components/_shared-components/ui-elements/SwarovskiModalWithImageWrapper";
import { NEWSLETTER_POPUP_DOM_EVENT_NAME } from "./NEWSLETTER_POPUP_DOM_EVENT_NAME";
import { ContentfulEntryEditorLink } from "./ContentfulEntryEditorLink";
import type { SwarovskiOptikPage } from "@swo/sitemap-service";

export type NewsletterPopupT = Translated<NewsletterTranslation>;

export const NewsletterPopup: ReactTFC<{
  locale: SwarovskiOptikLocaleFrontend;
  segment: SwarovskiOptikSegmentShort;
  newsletter: PageNewsletterData;
  links: { termsAndConditions: string; privacyPolicy: string };
  currentPageType: SwarovskiOptikPage["__typename"];
}> = props => {
  const [mode, setMode] = useState<"closed" | "sign-up" | "thank-you">("closed");
  const t = t2(props.newsletter.translations);

  useEffect(() => {
    const activateNewsletterPopup = () => {
      setMode("sign-up");
    };

    window.addEventListener(NEWSLETTER_POPUP_DOM_EVENT_NAME, activateNewsletterPopup);
    return () => {
      window.removeEventListener(NEWSLETTER_POPUP_DOM_EVENT_NAME, activateNewsletterPopup);
    };
  }, []);

  const hideModalCallback = useCallback(() => {
    setMode("closed");
  }, []);

  const newsletterSignUp = (email: string) => {
    return storefrontClient
      .newsletterSignUp({
        email,
        segments: [props.segment.key],
        selectedCountryIsoCode: getIsoCodeWithIntFallback(props.locale.country),
        selectedLanguageIsoCode: props.locale.language.isoCode,
      })
      .finally(() => {
        setMode("thank-you");
      });
  };

  const sharedProps = {
    modalIsOpen: mode !== "closed",
    hideModalCallback: hideModalCallback,
  };

  if (
    props.currentPageType === "CartPage" ||
    props.currentPageType === "CheckoutPage" ||
    props.currentPageType === "PurchaseConfirmationPage"
  ) {
    return null;
  }

  return (
    <SwarovskiModalWithVisualWrapper
      {...sharedProps}
      visual={{
        square: props.newsletter.visual.square,
        horizontal: props.newsletter.visual.horizontal,
      }}
    >
      <ContentfulEntryEditorLink entryId={props.newsletter.sysId}>
        {mode === "sign-up" && <NewsletterPopupSignUp newsletterSignUp={newsletterSignUp} t={t} links={props.links} />}
        {mode === "thank-you" && (
          <NewsletterPopupThankYou
            headline={t("newsletter.thankYou.headline")}
            paragraph={t("newsletter.thankYou.paragraph")}
            buttonLabel={t("newsletter.thankYou.buttonLabel")}
            buttonAction={() => setMode("closed")}
          />
        )}
      </ContentfulEntryEditorLink>
    </SwarovskiModalWithVisualWrapper>
  );
};
